import { fn } from '@ember/helper';
import Divider from 'tio-ui/components/divider';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { eq, not } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import ErrorMessages from 'tio-ui/components/error-messages';
import { PhCheckCircle, PhPencilSimple, PhWarning, PhXCircle } from 'ember-phosphor-icons';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';

interface StepDefinition {
  id: string;
  label: string;
  isComplete: boolean;
  summaryText: string;
  summaryKind?: 'course-cash';
}

interface StepSubmitSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    programTemplate: TasProgramTemplateModel;
    conditionsForSubmit: readonly ConditionModel[];
    acceptedConditions: ConditionModel[];
    canSubmitApplication: boolean;
    isSubmitting: boolean;
    submitError: string | null;
    hasGpaIfRequired: boolean;
    showGPAField: boolean;
    completedSteps: StepDefinition[];
    currentStep: number;
    onDidUpdateAcceptedConditions: (conditions: ConditionModel[]) => void;
    onSubmit: () => void;
    onEditStep: (index: number) => void;
  };
}

export default class StepSubmit extends Component<StepSubmitSignature> {
  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get reviewableSteps() {
    return this.args.completedSteps.filter((_step, index) => index !== this.args.currentStep);
  }

  get incompleteSteps() {
    return this.reviewableSteps.filter((step) => !step.isComplete);
  }

  get allPreviousStepsComplete() {
    return this.incompleteSteps.length === 0;
  }

  <template>
    <Section>
      <:header>
        <div class="tio-row-tight">
          <span
            class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand tio-fg-inverse tio-text-xs-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.benefits_summary.submit_application"}}</span>
        </div>
      </:header>
      <:body>
        <VStack @collapsed={{true}}>
          {{! Review summary of completed steps }}
          <div class="mb-4">
            <h3 class="font-semibold tio-fg-secondary mb-3">{{t
                "tas.guided.steps.review_title"
              }}</h3>
            {{#each this.reviewableSteps as |step index|}}
              <div class="tio-row py-2 border-b tio-border-subtle last:border-b-0">
                {{#if step.isComplete}}
                  <PhCheckCircle @size="20" @weight="fill" class="tio-fg-info shrink-0" />
                {{else}}
                  <span
                    class="tio-inline-row-center w-5 h-5 rounded-full tio-bg-danger-soft ring-2 tio-ring-danger shrink-0"
                  >
                    <PhXCircle @size="20" @weight="fill" class="tio-fg-danger" />
                  </span>
                {{/if}}
                <span
                  class="flex-1 text-sm
                    {{if step.isComplete 'tio-fg-secondary' 'font-medium tio-fg-danger-body'}}"
                >{{step.label}}</span>
                {{#if step.summaryText}}
                  <span class="text-xs {{if step.isComplete 'tio-fg-muted' 'tio-fg-danger'}}">
                    {{#if (eq step.summaryKind "course-cash")}}
                      <span aria-hidden="true">{{htmlSafe
                          (t "common.tas.course_cash.default")
                        }}</span>
                      <span class="sr-only">{{t "common.tas.course_cash.label"}}</span>
                    {{else}}
                      {{step.summaryText}}
                    {{/if}}
                  </span>
                {{/if}}
                <button
                  type="button"
                  class="tio-fg-brand hover:tio-fg-brand"
                  {{on "click" (fn @onEditStep index)}}
                >
                  <PhPencilSimple @size="16" />
                </button>
              </div>
            {{/each}}
          </div>

          <Divider class="my-4" />

          {{! GPA warning }}
          {{#if @showGPAField}}
            {{#unless @hasGpaIfRequired}}
              <div
                class="tio-bg-warning-soft border tio-border-warning rounded-lg p-3 mb-4 tio-row-top-tight"
              >
                <PhWarning @size="20" class="tio-fg-warning shrink-0 mt-0.5" />
                <p class="text-sm tio-fg-warning">
                  {{t "tas.guided.gpa_required"}}
                </p>
              </div>
            {{/unless}}
          {{/if}}

          {{! Conditions }}
          {{#if @conditionsForSubmit.length}}
            <div class="mb-4">
              <h3 class="tio-text-sm-medium tio-fg-secondary mb-2">{{t
                  "tas.guided.steps.terms_and_conditions"
                }}</h3>
              <div class="text-sm">
                <AcceptConditions
                  @conditions={{@conditionsForSubmit}}
                  @onChange={{@onDidUpdateAcceptedConditions}}
                />
              </div>
            </div>
          {{/if}}

          {{! Submit }}
          <div class="py-4 tio-stack-center">
            <Button
              @isRunning={{@isSubmitting}}
              @isDisabled={{not @canSubmitApplication}}
              @intent="primary"
              data-test-submit-application
              {{on "click" @onSubmit}}
            >
              {{t "tas.benefits_summary.submit_application"}}
            </Button>
            <p class="tio-text-sm-light text-center text-pretty mt-2">
              {{t "tas.benefits_summary.helper_text"}}
            </p>
            {{#if @submitError}}
              <ErrorMessages @showErrors={{true}} @error={{@submitError}} class="text-sm mt-2" />
            {{/if}}
          </div>
        </VStack>
      </:body>
    </Section>
  </template>
}
